<template>
  <v-form v-model="isFormValid" class="aveine-referential-form">
    <v-container v-if="dataLoaded">

      <!-- Nom traduit. Le champ principal edite la locale courante ; la
           pastille « traduire » ouvre les neuf langues d'un coup, comme le
           formulaire des domaines. -->
      <v-row class="mr-1 ml-1">
        <v-text-field
          :rules="[v => !!v || $t('intranet.referential.validation.nameRequired')]"
          v-model="item.name[$i18n.locale]"
          :label="$t('intranet.referential.attributes.name')"
        >
          <template v-slot:append>
            <v-dialog transition="dialog-bottom-transition">
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" icon color="black">
                  <v-icon>mdi-translate</v-icon>
                </v-btn>
              </template>
              <template v-slot:default="dialog">
                <v-card>
                  <v-toolbar class="pl-6 text-h6" color="primary" dark>
                    {{$t('intranet.referential.attributes.name')}}
                    <v-spacer></v-spacer>
                    <v-btn icon @click="dialog.value = false">
                      <v-icon color="white">mdi-close</v-icon>
                    </v-btn>
                  </v-toolbar>
                  <v-card-text class="justify-center align-center pa-9">
                    <!-- Une boucle plutot que neuf blocs recopies : ajouter une
                         locale ne demandera qu'une entree dans `locales`. -->
                    <v-text-field
                      v-for="locale in locales"
                      :key="locale"
                      class="mx-2"
                      v-model="item.name[locale]"
                      :label="locale.toUpperCase()"
                    ></v-text-field>
                    <div class="d-flex justify-center">
                      <v-btn class="md-raised" depressed color="primary" @click="dialog.value = false">
                        {{$t('intranet.referential.button.save')}}
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </template>
            </v-dialog>
          </template>
        </v-text-field>
      </v-row>

      <!-- Regroupement : `family` pour les aromes, `category` pour les mets.
           Champ libre avec suggestions, et non liste fermee : le referentiel
           n'a pas de table de familles, la valeur est une simple colonne texte
           (voir la migration 2026-08-17--10). Un v-select interdirait d'en
           creer une nouvelle. -->
      <v-row class="mr-1 ml-1 mt-6">
        <v-combobox
          v-model="item[groupField]"
          :items="groupSuggestions"
          :label="groupLabel"
          clearable
        ></v-combobox>
      </v-row>

      <v-divider class="my-9"></v-divider>

      <h3>{{$t('intranet.referential.title.image')}}</h3>
      <v-col class="ma-xl-6 mt-6">
        <v-row class="d-flex align-center justify-center">
          <v-col>
            <div class="d-flex justify-center mb-6">
              <v-btn depressed color="primary" @click="$refs.imageInput.click()">
                {{$t('intranet.referential.button.uploadFile')}}
              </v-btn>
              <input
                ref="imageInput"
                type="file"
                class="d-none"
                accept="image/png,image/jpeg"
                @change="loadImageFile"
              >
            </div>
            <v-row v-if="item.image" class="d-flex justify-center my-4">
              <v-btn text @click="deleteImage()">
                {{$t('intranet.referential.button.deleteImage')}}
              </v-btn>
            </v-row>
          </v-col>
          <v-row v-if="item.image" class="flex-grow-0 flex-shrink-1">
            <img
              class="image-preview"
              :alt="$t('intranet.referential.title.image')"
              :src="item.displayed_image ? item.displayed_image : item.image"
            >
          </v-row>
        </v-row>
      </v-col>

      <v-divider class="my-9"></v-divider>

      <div class="d-flex justify-center">
        <v-btn class="md-raised" :disabled="!isFormValid" depressed color="primary" @click="submit()">
          {{$t('intranet.referential.button.submit')}}
        </v-btn>
      </div>
    </v-container>

    <div v-else class="d-flex justify-center align-center mt-16">
      <v-progress-circular indeterminate :size="100" :width="10" color="secondary"></v-progress-circular>
    </div>
  </v-form>
</template>

<script>
import { errorHandler } from 'aveine-toolbox'

/**
 * Formulaire d'un referentiel de degustation : arome ou accord mets-vins.
 *
 * Un seul composant pour les deux plutot qu'un par ressource : ils ont la meme
 * forme au nom du champ de regroupement pres (`family` / `category`). Un
 * troisieme referentiel de cette famille ne demanderait qu'une page de plus.
 */
export default {
  name: 'AveineReferentialForm',
  props: {
    /** La classe vuex-orm (Aroma ou FoodPairing). */
    model: { type: Function, required: true },
    /** La ressource editee. */
    item: { type: Object, required: true },
    /** Le segment d'URL de l'API : `aromas` ou `food-pairings`. */
    resource: { type: String, required: true },
    /** La cle vuex-orm dans la reponse : `aromas` ou `food_pairings`. */
    entity: { type: String, required: true },
    /** `family` pour les aromes, `category` pour les mets. */
    groupField: { type: String, required: true },
    /** Libelle traduit du champ de regroupement. */
    groupLabel: { type: String, required: true },
    /** Valeurs deja utilisees, proposees en autocompletion. */
    groupSuggestions: { type: Array, default: () => [] },
    APIbaseURL: String
  },
  data: () => ({
    isFormValid: false,
    dataLoaded: false,
    locales: ['fr', 'en', 'it', 'de', 'es', 'ja', 'ko', 'zh', 'nl']
  }),
  created () {
    // `name` arrive en objet vide sur une creation. Sans cette garde, le
    // v-model sur item.name[locale] planterait sur un undefined.
    if (!this.item.name) {
      this.$set(this.item, 'name', {})
    }
    this.dataLoaded = true
  },
  methods: {
    /**
     * Lit le fichier choisi, en verifie le poids et le type, et le garde de
     * cote. `displayed_image` porte l'apercu base64 ; `image` porte le File.
     * C'est la presence de `displayed_image` qui signale, au moment de
     * l'envoi, qu'une NOUVELLE image a ete choisie — sans quoi on renverrait
     * au serveur l'URL signee recue en lecture.
     */
    loadImageFile (blob) {
      const file = blob.target.files[0]
      if (!file) return

      if (file.size > 2097152) {
        this.$toastr.e(this.$t('intranet.referential.validation.imageSize'))
        this.item.displayed_image = null
        return
      }
      if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
        this.$toastr.e(this.$t('intranet.referential.validation.imageType'))
        this.item.displayed_image = null
        return
      }

      const reader = new FileReader()
      reader.onload = () => { this.$set(this.item, 'displayed_image', reader.result) }
      reader.readAsDataURL(file)
      this.$set(this.item, 'image', file)
    },

    /**
     * Retire la vignette.
     *
     * Sur une ressource deja enregistree, l'appel part tout de suite vers
     * DELETE /<resource>/:id/image, et non au moment du submit : un PATCH ne
     * peut pas exprimer ce retrait, `image` etant ignore a la deserialisation
     * cote API. Sur une creation pas encore envoyee, il n'y a rien a appeler.
     */
    async deleteImage () {
      if (!this.item.$isNew && this.item.id) {
        try {
          await this.model.api().delete(
            `${this.resource}/${this.item.id}/image`,
            { baseURL: this.APIbaseURL, save: false }
          )
        } catch (errors) {
          errorHandler(errors, this)
          return
        }
      }
      this.$set(this.item, 'image', null)
      this.$set(this.item, 'displayed_image', null)
    },

    async submit () {
      try {
        this.dataLoaded = false

        const formData = { document: this.item }
        if (this.item.displayed_image) {
          formData.attachments = { attachment_image: this.item.image }
          this.item.image = null
        }

        const url = this.item.$isNew
          ? `${this.resource}?lang=${this.$i18n.locale}`
          : `${this.resource}/${this.item.id}?lang=${this.$i18n.locale}`

        const response = this.item.$isNew
          ? await this.model.api().post(url, formData, { baseURL: this.APIbaseURL })
          : await this.model.api().patch(url, formData, { baseURL: this.APIbaseURL })

        this.$toastr.s(this.$t('intranet.referential.title.saveConfirmation'))
        this.$router.push(`/authenticated/${this.resource}/list`)
        return response
      } catch (errors) {
        this.dataLoaded = true
        errorHandler(errors, this)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .image-preview {
    max-width: 160px;
    max-height: 160px;
  }
</style>
